import { jwtDecode } from 'jwt-decode';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { Authorization, User, CompanyProfile, ServerJWTAuthorization, JWTTokenPayload } from 'types';
import { usersApiSlice } from 'services/users';

const tokenType : string = "Bearer"; 

export interface AuthState {
    authorization: Authorization | null;
    user: User | null;
    status: 'idle' | 'loading' | 'failed';
    realmId: number | null;
    selected_realm: CompanyProfile | null;
}

const initialState: AuthState = {
    authorization: (localStorage.getItem('accessJWT') ? 
      ({ accessToken: localStorage.getItem('accessJWT'),
        refreshToken: localStorage.getItem('refreshJWT'),
        tokenType: tokenType,
      }) as Authorization : null),
    user: null,
    realmId: null,
    status: 'idle',
    selected_realm: null,
};

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    tokensReceived: (state, action: PayloadAction<ServerJWTAuthorization>) => {
      state.authorization = ({
        accessToken: action.payload.access,
        refreshToken: action.payload.refresh,
        tokenType: "Bearer",
      }) as Authorization;
      localStorage.setItem('accessJWT', action.payload.access);
      localStorage.setItem('refreshJWT', action.payload.refresh);
      localStorage.setItem('tokenType', "Bearer");
      const decodedToken: any = jwtDecode(action.payload.access);
      state.realmId = decodedToken.realm || null;
    },
    setUserData: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    logout: (state) => {
      localStorage.removeItem('accessJWT');
      localStorage.removeItem('refreshJWT');
      localStorage.removeItem('tokenType');
      return {
        ...state,
        authorization: null,
        user: null,
        realmId: null,
      } as AuthState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(usersApiSlice.endpoints.loginJWT.matchFulfilled, (state, action) => {
        state.authorization = ({
          accessToken: action.payload.access,
          refreshToken: action.payload.refresh,
          tokenType: "Bearer",
        }) as Authorization;
        localStorage.setItem('accessJWT', action.payload.access);
        localStorage.setItem('refreshJWT', action.payload.refresh);
        localStorage.setItem('tokenType', "Bearer");
      })
      .addMatcher(usersApiSlice.endpoints.googleLogin.matchFulfilled, (state, action) => {
        state.authorization = ({
          accessToken: action.payload.access_token,
          expiresIn: action.payload.expires_in,
          refreshToken: action.payload.refresh_token,
          scope: action.payload.scope,
          tokenType: action.payload.token_type
        }) as Authorization;
        localStorage.setItem('accessToken', action.payload.access_token);
        localStorage.setItem('refreshToken', action.payload.refresh_token);
        localStorage.setItem('tokenType', action.payload.token_type);
      })
      .addMatcher(usersApiSlice.endpoints.getUserData.matchFulfilled, (state, action) => {
        state.user = ({
          id: action.payload.id,
          username: action.payload.username,
          email: action.payload.email,
          is_company: action.payload.is_company,
          phone: action.payload.phone,
          realms: action.payload.realms,
          user_profile: action.payload.user_profile,
        }) as User;
        state.selected_realm = action.payload.realms?.[0] || null;
      })
      .addMatcher(usersApiSlice.endpoints.selectRealm.matchFulfilled, (state, action) => {
        state.authorization = ({
          accessToken: action.payload.access,
          refreshToken: action.payload.refresh,
          tokenType: "Bearer",
        }) as Authorization;
        localStorage.setItem('accessJWT', action.payload.access);
        localStorage.setItem('refreshJWT', action.payload.refresh);
        localStorage.setItem('tokenType', "Bearer");
        const decodedToken: JWTTokenPayload = jwtDecode(action.payload.access);
        state.realmId = decodedToken.realm_id;
      })
      .addMatcher(usersApiSlice.endpoints.logout.matchFulfilled, (state) => {
        state.authorization = null;
        state.user = null;
        localStorage.removeItem('accessJWT');
        localStorage.removeItem('refreshJWT');
        localStorage.removeItem('tokenType');
      })
  },
});

export const { tokensReceived, setUserData, logout } = usersSlice.actions;

export const selectAccessToken = (state: RootState) => state.users.authorization?.accessToken
export const selectUserIsCompany = (state: RootState) => state.users.user?.is_company;
export const selectRealmId = (state: RootState) => state.users.realmId;

export default usersSlice.reducer;
